import { useCallback } from "react";
import toast from "react-hot-toast";

import { useAmityAuth, useTranslate } from "@/providers/context";
import { isCommunityAdmin } from "../util";

const containsUrl = (text: string) => {
  const re =
    /(\b(?:https?:\/\/)?(?:www\.)?[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}(?:\/\S*)?)\b/i;
  return re.test(text);
};

const containsEmail = (text: string) => {
  const re = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/;
  return re.test(text);
};

export const useCommunityError = () => {
  const { translate } = useTranslate();
  const { authState } = useAmityAuth();

  const showToast = useCallback(
    (message: string) => {
      toast(`${translate(message)}. ${translate("Please try again later")}.`);
    },
    [translate],
  );

  const textContainsBlockedContent = useCallback(
    (text: string) => {
      // Admins can post URLs and emails
      if (isCommunityAdmin(authState.authenticatedUser)) {
        return false;
      }

      // Otherwise, we block them on the client side
      return containsUrl(text) || containsEmail(text);
    },
    [authState.authenticatedUser],
  );

  const showBlockedContentToast = useCallback(() => {
    toast(
      `${translate("URLs and emails are not allowed to be posted")}. ${translate("Please remove and try again")}.`,
    );
  }, [translate]);

  const handlePostingError = (
    e: Error & { code?: number },
    isComment?: boolean,
  ) => {
    if (e.code === 400309) {
      // The whitelist feature is meant to be turned off in Amity as we block them on the client side
      // But if it hasn't been turned off we will receive this error (for admins as well)
      showBlockedContentToast();
    } else {
      showToast(
        isComment ? "Failed to upload comment" : "Failed to upload post",
      );
    }
    throw e;
  };

  const handlePinError = (e: Error, isPinning: boolean) => {
    showToast(isPinning ? "Failed to pin post" : "Failed to unpin post");
    throw e;
  };

  const handleDeletePostError = (e: Error, isComment?: boolean) => {
    showToast(isComment ? "Failed to delete comment" : "Failed to delete post");
    throw e;
  };

  const handleBlockUserError = (e: Error) => {
    showToast("Failed to block user");
    throw e;
  };

  const handleReportError = (e: Error, isRemoving?: boolean) => {
    showToast(isRemoving ? "Failed to remove report" : "Failed to add report");
    throw e;
  };

  const handleGenericCommunityError = (e: Error) => {
    showToast("Something went wrong");
    console.error(e);
  };

  return {
    textContainsBlockedContent,
    showBlockedContentToast,
    handlePostingError,
    handlePinError,
    handleDeletePostError,
    handleBlockUserError,
    handleReportError,
    handleGenericCommunityError,
  };
};
